import { ButtonGhost } from '@fukku/button/Ghost'
import { OverDialogLayer } from '@fukku/dialog/OverDialogLayer'
import text from '@fukku/styles/texts.module.scss'
import { IconCloseMedium } from 'icons/components/IconCloseMedium'
import { type AnimationEvent, useCallback, useEffect, useRef } from 'react'
import { cx } from 'utils/cx'
import { SnackbarPosition } from '../../../constants/constants'
import styles from '../Snackbar.module.scss'

interface LargeSnackbarProps {
	readonly id: string
	readonly title: string
	readonly children: React.ReactNode
	readonly status: string
	readonly position: SnackbarPosition
	readonly snackbarRef: React.RefObject<HTMLDivElement | null>
	readonly snackbarStyle: React.CSSProperties
	readonly handleIn: () => void
	readonly handleOut: () => void
	readonly handleOnAnimationEnd: (e: AnimationEvent<HTMLElement>) => void
	readonly dataTestId?: string
	readonly onClose?: () => void
	readonly opened?: boolean
	readonly removeNotification: () => void
}

const ICON_SIZE = 16
const SWIPE_THRESHOLD = 50

export const deactivateSnackbarScroll = (
	snackbarElement: HTMLElement | null
) => {
	if (snackbarElement) {
		snackbarElement.style.overflow = 'hidden'
		snackbarElement.style.touchAction = 'none'
	}
}

export const activateSnackbarScroll = (snackbarElement: HTMLElement | null) => {
	if (snackbarElement) {
		snackbarElement.style.overflow = ''
		snackbarElement.style.touchAction = ''
	}
}

export const LargeSnackbar = ({
	id,
	title,
	children,
	status,
	position,
	snackbarRef,
	snackbarStyle,
	handleIn,
	handleOut,
	handleOnAnimationEnd,
	dataTestId,
	onClose,
	opened,
	removeNotification,
}: LargeSnackbarProps) => {
	const touchStartY = useRef<number | null>(null)

	const handleTouchStart = useCallback(
		(event: React.TouchEvent<HTMLDivElement>) => {
			touchStartY.current = event.touches[0].clientY
			deactivateSnackbarScroll(snackbarRef.current)
		},
		[]
	)

	const handleTouchMove = useCallback(
		(event: React.TouchEvent<HTMLDivElement>) => {
			if (touchStartY.current === null) {
				return
			}

			const touchEndY = event.touches[0].clientY
			const deltaY = touchEndY - touchStartY.current

			if (deltaY > SWIPE_THRESHOLD) {
				if (onClose) {
					onClose()
				}
				if (snackbarRef.current) {
					snackbarRef.current.classList.add(styles.out)
				}
				touchStartY.current = null
			}
		},
		[onClose, snackbarRef]
	)

	useEffect(() => {
		if (opened) {
			deactivateSnackbarScroll(snackbarRef.current)
		} else {
			activateSnackbarScroll(snackbarRef.current)
		}

		return () => {
			activateSnackbarScroll(snackbarRef.current)
		}
	}, [opened])

	useEffect(() => {
		if (!opened) {
			return () => {}
		}

		const handleOutsideClick = (event: MouseEvent) => {
			if (
				snackbarRef.current &&
				!snackbarRef.current.contains(event.target as Node)
			) {
				onClose?.()
				removeNotification()
				snackbarRef.current.classList.add(styles.out)
			}
		}

		document.addEventListener('click', handleOutsideClick)
		return () => {
			document.removeEventListener('click', handleOutsideClick)
		}
	}, [opened, onClose, removeNotification, snackbarRef])

	return (
		<OverDialogLayer>
			<div
				id={`${id}-large-snackbar`}
				className={cx(
					styles.largeSnackbarWrapper,
					!opened && styles.open,
					styles[status],
					position === SnackbarPosition.TOP && styles.smallTop
				)}
				ref={snackbarRef}
				onTouchStart={handleTouchStart}
				onTouchMove={handleTouchMove}
				onAnimationEnd={handleOnAnimationEnd}
				style={snackbarStyle}
				role='alert'
				data-testid={
					dataTestId
						? `${dataTestId}-large-snackbar`
						: 'large-snackbar-notification'
				}
			>
				<div
					className={styles.snackbarElement}
					onPointerEnter={handleIn}
					onPointerLeave={handleOut}
				>
					<div className={styles.largeWrapperElements}>
						<p
							id={`${id}-title`}
							className={cx(text.uppercaseM, styles.largeTitle)}
						>
							{title}
						</p>
						<ButtonGhost
							type='button'
							className={styles.closeButton}
							onClick={() => {
								onClose?.()
								removeNotification()
								if (snackbarRef.current) {
									snackbarRef.current.classList.add(styles.out)
								}
							}}
							aria-label='Close'
							data-testid={dataTestId ? `${dataTestId}-close` : 'close'}
						>
							<IconCloseMedium width={ICON_SIZE} height={ICON_SIZE} />
						</ButtonGhost>
					</div>
					<div className={styles.largeNotificationBody}>{children}</div>
				</div>
			</div>
		</OverDialogLayer>
	)
}
